import { Camera, FileText, Loader2, Package, Search } from 'lucide-react'

import { useSearchActions, useSearchState } from '../stores/search'

import { useUIStore } from '@/stores/useUIStore'
import {
  isDingTalkEnvironment,
  isMobile,
  scanQRCodeInDingTalk
} from '@/utils/dingtalk'

const SearchForm = () => {
  const { showError } = useUIStore()
  const { loading, querySearch, searchResult } = useSearchState()
  const { openLoading, closeLoading, setQuerySearch, fetchQueryResult } =
    useSearchActions()

  // 检测是否在钉钉移动端环境
  const isDingDingMobile = () => {
    return isDingTalkEnvironment() && isMobile()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchWithQuery()
    }
  }

  // 处理扫描功能
  const handleScan = async () => {
    if (!isDingDingMobile()) {
      showError('扫描功能仅在钉钉移动端可用')
      return
    }

    try {
      const scanResult = await scanQRCodeInDingTalk()
      setQuerySearch(scanResult)
      await handleSearchWithQuery()
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '扫描失败'
      showError(errorMessage)
    }
  }

  const handleSearchWithQuery = async () => {
    const query = querySearch.trim()
    if (!query) {
      showError('请输入查询内容')
      return
    }

    openLoading()
    try {
      await fetchQueryResult()
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '查询失败'
      showError(errorMessage)
    } finally {
      closeLoading()
    }
  }

  return (
    <>
      {/* 搜索区域 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-row items-center">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center whitespace-nowrap">
            <Search className="w-6 h-6 text-blue-500 mr-2" />
            智能搜索
          </h2>
          <span className="text-sm text-gray-500 ml-2">
            支持唛头、托盘编号、箱货编号、库位编号混合查询
          </span>
        </div>

        <div className="p-6">
          {/* 搜索输入框 */}
          <div className="relative">
            {/* PC端一行显示，移动端两行显示 */}
            <div className="flex flex-col md:flex-row gap-4">
              {/* 输入框 */}
              <div className="flex-1">
                <input
                  id="search-input"
                  type="text"
                  value={querySearch}
                  onChange={(e) => setQuerySearch(e.target.value.trim())}
                  onKeyDown={handleKeyDown}
                  placeholder="请输入查询内容..."
                  disabled={loading}
                  className={`w-full px-4 py-4 text-xl border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                    loading
                      ? 'border-yellow-300 bg-yellow-50'
                      : querySearch
                      ? 'border-blue-300 bg-blue-50 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                  }`}
                />
              </div>

              {/* 按钮组 */}
              <div className="flex flex-row gap-3 flex-shrink-0">
                {/* 扫描按钮 - 仅在钉钉移动端显示 */}
                {isDingDingMobile() && (
                  <button
                    onClick={handleScan}
                    disabled={loading}
                    className="w-full sm:w-auto py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 bg-amber-600 text-white hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    <Camera className="w-5 h-5" />
                    <span>扫描</span>
                  </button>
                )}

                <button
                  onClick={handleSearchWithQuery}
                  disabled={loading || !querySearch}
                  className="w-full sm:w-auto py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 bg-cyan-600 text-white hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>查询中...</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      <span>查询</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 使用说明 */}
      {!searchResult && !loading && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-blue-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <FileText className="w-6 h-6 text-blue-500 mr-2" />
              使用说明
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Search className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">智能搜索</h4>
                    <p className="text-gray-600 text-sm">
                      支持唛头、托盘编号、箱货编号、库位编号混合查询
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Camera className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">一键扫描</h4>
                    <p className="text-gray-600 text-sm">
                      在钉钉移动端可使用扫描功能快速输入
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">实时结果</h4>
                    <p className="text-gray-600 text-sm">
                      自动识别输入类型并显示相关信息
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <Package className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">多类型展示</h4>
                    <p className="text-gray-600 text-sm">
                      同一查询可能返回多种类型的匹配结果
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                💡 提示：支持回车键快速搜索，输入任意内容即可开始查询
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SearchForm
